/**
 * App-level access. `NONE` is the default when a user has neither a
 * team nor user-level grant for the app — surfaced explicitly so apps
 * can render "no access" instead of inferring it from a missing key.
 */
export type AppRole = 'VIEWER' | 'EDITOR' | 'ADMIN' | 'NONE'

/**
 * Identity-kind of an actor.
 *
 * - `INTERNAL` / `EXTERNAL` / `BOT` are persisted `AuthUser` rows in
 *   Multipass. Bots are real users (the `ticket-bot` / `coder-bot`
 *   factory agents); they sign in and act on their own behalf.
 * - `SERVICE` is never persisted. It tags synthetic actors that
 *   middleware materializes at request time when an Ordo app calls
 *   another with a service JWT instead of a session cookie. Services
 *   are distinct from bots: a bot is *someone*, a service is the
 *   calling app itself, with no user identity attached.
 */
export type UserType = 'INTERNAL' | 'EXTERNAL' | 'BOT' | 'SERVICE'

/**
 * Three-shade team color resolved from multipass's palette. Apps render
 * directly from these hex values — no palette lookup required client-side.
 */
export interface TeamColor {
  /** Palette name, e.g. "Red" / "Cobalt". Stable identifier. */
  name: string
  /** Tag background. */
  light: string
  /** Standalone icon on white or dark. */
  primary: string
  /** Tag text on light background. */
  dark: string
}

export interface PublicUserTeam {
  id: string
  name: string
  slug: string
  color: TeamColor
}

/**
 * One person on the HQ staff roster, as one app sees them — the row shape
 * of multipass `GET /api/service/team-roster` and of every internal app's
 * shared read-only Team page. HQ is the only place any of this is edited.
 */
export interface TeamRosterMember {
  id: string
  email: string
  name: string | null
  picture: string | null
  /** False = archived (deactivated in HQ). */
  active: boolean
  /** The user's one sub-team (Growth / Ops / Product…). Never the `hq` base team. */
  subTeam: PublicUserTeam | null
  /**
   * Active personas held (e.g. `hq-admin`, `signatory`), name-ascending.
   * `grantsAdmin` is absent from multipass deploys before 2026-10-16.
   */
  personas: { slug: string; name: string; grantsAdmin?: boolean }[]
  /** Effective role on the requested app; null = no access. */
  role: Exclude<AppRole, 'NONE'> | null
  /*
   * Added 2026-10-16 for the shared Team table's Sign-in and Last seen
   * columns. Optional: apps can read a multipass that predates them.
   */
  /** The user's own HQ admin flag. */
  isAdmin?: boolean
  /** ISO timestamp of the last sign-in; null = never signed in. */
  lastLoginAt?: string | null
  /** A Google Workspace account is linked (they sign in with Google). */
  hasGoogleIdentity?: boolean
}

/**
 * One archived departed staff member — the row shape of multipass
 * `GET /api/service/users/archived` (staff archive, 2026-10-15). Apps that
 * keep local user rows strip these users' local access in a sweep; see
 * `runArchivedStaffSweep`.
 */
export interface ArchivedStaffEntry {
  /** Multipass user id — what apps store as `multipassUserId` / `multipassId`. */
  id: string
  email: string
  /** ISO timestamp of the archive. */
  archivedAt: string
}

/**
 * Per-app entry on the public-user payload. Carries the canonical app
 * branding (name, url, icon, colors) inline with the user's effective
 * role so consumers can render an "other apps you can access" launcher
 * — filtering by `role !== 'NONE'` — without a second fetch.
 *
 * Surfaced on `PublicUser.appRoles` alongside the bare-string `roles`
 * map. New code that needs branding reads `appRoles`; every existing
 * consumer keeps reading `roles` unchanged.
 */
export interface AppRoleEntry {
  id: string
  slug: string
  name: string
  description: string | null
  url: string | null
  icon: string | null
  bgColor: string | null
  fgColor: string | null
  /** Hidden from every switching surface — sidebar app rail, Cmd+K app
   *  switcher, AND the home "My Apps" tile grid (`getLauncherApps`
   *  filters it everywhere; only `getAccessibleApps` still returns it).
   *  Optional in the type so older multipass deploys that don't emit
   *  the field still validate; readers should default to `false`. */
  hidden?: boolean
  /** Admin-set launcher position (HQ → Apps → Sort). `null`/absent =
   *  unordered: sorts after every ordered app, then by name. Optional so
   *  payloads from multipass deploys that predate it still validate. */
  sortOrder?: number | null
  role: AppRole
}

/**
 * Resolved per-user configuration. Multipass parses each value per its
 * registered `ConfigValueType` so apps see typed scalars, not strings.
 * New keys can be added at the data layer without bumping `@ordo/auth` —
 * apps that don't know a key get the value as-is.
 */
export type PublicUserConfigValue = string | boolean
export type PublicUserConfig = Record<string, PublicUserConfigValue>

/**
 * Stable string constants for the config keys multipass ships at launch.
 * Apps reference these instead of typing the slug literal everywhere.
 */
export const CONFIG_KEYS = {
  COLOR_MODE: 'color_mode',
  EMAIL_NOTIFICATIONS_ENABLED: 'email_notifications_enabled',
  VOICE_INPUT_ENABLED: 'voice_input_enabled',
} as const

/**
 * Public-facing user shape returned by multipass. Mirrors the AuthUser
 * model minus any sensitive fields (password hash and token data live
 * in sibling tables and are never serialized into this shape) and adds
 * derived fields populated from the user-management domain (teams, tags,
 * effective per-app roles, config). All fields after `updatedAt` are
 * additive — older apps reading just `id`/`email`/`isAdmin` are unaffected.
 */
export interface PublicUser {
  id: string
  email: string
  name: string | null
  givenName: string | null
  familyName: string | null
  picture: string | null
  locale: string | null
  googleId: string | null
  /** Multipass admin (manage users, apps and access): the account flag OR
   *  a held persona that grants admin (e.g. `hq-admin`). */
  isAdmin: boolean
  active: boolean
  lastLoginAt: string | null
  createdAt: string
  updatedAt: string
  userType: UserType
  /** True when the user has no team assignment. */
  pending: boolean
  /** The user's single team, or null when pending. */
  team: PublicUserTeam | null
  tags: string[]
  /**
   * App slug → effective role. Every known app appears in this map;
   * apps the user has no access to are `'NONE'`. Effective role is the
   * max of team-default and user-override (overrides escalate, never
   * demote a higher team grant).
   */
  roles: Record<string, AppRole>
  /**
   * App slug → enriched entry carrying the canonical app branding (name,
   * url, icon, colors) inline with the effective role. Same role
   * resolution as `roles` above, just enriched. Optional in the type so
   * older multipass deploys (or callers that ship a hand-rolled
   * `PublicUser`) keep validating; readers should fall back to `roles`
   * when this is absent.
   */
  appRoles?: Record<string, AppRoleEntry>
  /**
   * Slugs of the active personas (suite access bundles, assigned in HQ) the
   * user holds — their grants are already folded into `roles`/`appRoles`.
   * Optional so payloads from multipass deploys that predate personas still
   * validate; treat absent as `[]`.
   */
  personas?: string[]
  config: PublicUserConfig
}

export interface ValidationResult {
  valid: boolean
  user?: PublicUser
}

/**
 * Which sign-in methods the session user has configured, as reported by
 * `GET /api/auth/me/sign-in-methods`. `googleLinked` covers both
 * sign-in-mode Google users (`AuthUser.googleId`) and link-mode users
 * (an `OAuthIdentity` row only).
 */
export interface SignInMethods {
  hasPassword: boolean
  googleLinked: boolean
}

export interface WrappedOAuthToken {
  /** Multipass-signed JWT containing the access token + metadata. */
  token: string
  expiresAt: string
}

/**
 * The decoded payload inside the multipass-signed JWT returned by
 * `GET /api/auth/oauth/token`. Apps verify the JWT signature locally
 * (using the shared TOKEN_SECRET) before trusting these claims.
 */
export interface OAuthTokenClaims {
  sub: string
  provider: string
  accessToken: string
  expiresAt: string
  scopes: string[]
  iss: 'multipass'
  iat?: number
  exp?: number
}

export type OAuthProvider = 'google'

/**
 * A Google Workspace token acting as one staff member, minted by multipass's
 * domain-wide-delegated service account (`POST /api/service/google/workspace-token`).
 * Only read-only scopes multipass allowlists can be asked for.
 */
export interface WorkspaceAccessToken {
  accessToken: string
  /** ISO instant. */
  expiresAt: string
}

export interface LoginUrlOptions {
  /** Where multipass should redirect the user after sign-in. */
  redirect: string
  /** Extra OAuth scopes (e.g. Google Calendar) beyond the base profile scopes. */
  scopes?: string[]
  /**
   * Short-lived JWT (see `MultipassClient#mintOAuthLinkIntent`) that
   * switches multipass from sign-in semantics to link semantics: the
   * resulting OAuth identity is attached to the already-authenticated
   * user rather than upserting a new AuthUser. Used so an app can grant
   * additional provider scopes (Calendar, Drive, …) to a user who
   * signed in via some other method (magic link, credentials, a
   * different Google account) without touching their identity.
   */
  linkSession?: string
}

/**
 * Decoded claims from the short-lived handoff JWT that multipass
 * appends to the post-auth redirect URL as `?ordo_session_token=...`.
 * Apps on domains that can't see the multipass session cookie (e.g.,
 * ngrok hosts) use this to claim the session into a local cookie.
 */
export interface HandoffClaims {
  /** User id. */
  sub: string
  /** Multipass session id. The app stores this in its own domain cookie. */
  sid: string
  /**
   * Snapshot of the authenticated user at the moment of sign-in. Apps
   * typically use this to upsert their own local user row (matching on
   * `sub` or `email`) before assigning it as an FK.
   */
  user: PublicUser
  iss: 'multipass'
  purpose: 'handoff'
  iat?: number
  exp?: number
}

/** Query-parameter name multipass uses on the post-auth redirect. */
export const HANDOFF_TOKEN_PARAM = 'ordo_session_token'

/**
 * Query-parameter name multipass uses on the post-link redirect, carrying a
 * single-use code for a grant it is holding until the app confirms which
 * session is claiming it. See `MultipassClient#confirmOAuthLink`.
 */
export const LINK_CODE_PARAM = 'ordo_link_code'

/** Result of a successful `confirmOAuthLink` call. */
export interface ConfirmedOAuthLink {
  linked: true
  provider: string
  /** Scopes actually granted — check this before calling a provider API. */
  scopes: string[]
}
